import {
    Spinner,
    Table,
    Badge,
    Card,
    CardBody,
    Row,
    Col,
    Progress,
    CardFooter,
} from "reactstrap";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { API_BASE_URL } from "../../api/api";
import { useState } from "react";
import { UserOrdersModal } from "../influencerBonus/UserOrdersModal";
import { Btn } from "../../../AbstractElements";
import { useRanks } from "../../Hooks/use-ranks";
import { usePointValue } from "../../Hooks/use-point-value";
import { formatDate } from "../../utils/formatDate";

const DISPLAY_CURRENCY = "COP";

const formatCurrency = (value, currency) => {
    if (!value) return "$0";

    const symbols = {
        COP: "$",
        USD: "$",
        USDT: "$",
        EUR: "€",
    };

    const symbol = symbols[currency] || "$";

    return `${symbol} ${Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })}`;
};

const LoyaltyTab = ({ user }) => {
    const { getData } = useDataFetch();

    const [selectedOrderIds, setSelectedOrderIds] = useState([]);

    const validOrderIds = selectedOrderIds || [];

    const pointValue = usePointValue(DISPLAY_CURRENCY);

    const { data, isLoading, error } = getData(
        ["user-loyalty-bonus", user?.id],
        "/order/admin/calculate/monthly/loyalty_points",
        {
            enabled: !!user?.id,
        },
        {
            user_id: user?.id,
        }
    );

    const { data: ordersResponse, isPending: loadingOrders } = getData(
        ["orders_by_ids", validOrderIds],
        `/order/byOrders/ids?ids=${validOrderIds.join(",")}`,
        { enabled: validOrderIds.length > 0 }
    );

    // currentPointsRank y no currentRank: el segundo sale del histórico de
    // `userranks` y devuelve el rango MÁS ALTO que el usuario haya alcanzado
    // en cualquier corte, así que alguien que llegó a Diamante hace meses
    // seguía apareciendo como Diamante sin tener puntos este mes. El primero
    // se deriva de los puntos vigentes, que es lo que esta vista muestra.
    const {
        currentUserPoints,
        currentPointsRank,
        nextPointsRank,
        pointsToNextRank
    } = useRanks(user?.id);

    const currentRankName = currentPointsRank?.name || "Sin rango";

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner color="success" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center text-danger py-5">
                {error.message}
            </div>
        );
    }

    const pointsData = data?.data?.pointsData;


    const levels = data?.data?.levels || [];


    if (!pointsData) {
        return (
            <div className="text-center py-5">
                No hay información disponible.
            </div>
        );
    }

    /**
     * Los puntos salen de `ordersByLevel`, no de `totalLoyaltyPointsByLevel`.
     *
     * El segundo viene pisado en order-ms por `userpointscaches`, que sólo se
     * recalcula 3 veces al día: toda orden pagada después de la última corrida
     * salía en la lista de abajo pero no en el total, y la tabla decía 360 con
     * 960 en órdenes a la vista. `ordersByLevel` es el cálculo en vivo y es la
     * MISMA fuente de las órdenes que abre el modal, así que el número cuadra
     * con lo que se está mostrando.
     *
     * La caché sigue mandando donde debe: la columna de puntos del listado de
     * usuarios, el reporte de red y el Excel.
     */
    const totalLoyaltyPointsByLevel = Object.fromEntries(
        Object.entries(pointsData.ordersByLevel || {}).map(
            ([level, detail]) => [level, detail?.points_by_currency || {}]
        )
    );

    const levelOneActive = pointsData.levelOneActive || 0;

    const hasEnoughActiveUsers = levelOneActive >= 3;

    /**
     * Un nivel está desbloqueado si el plan lo tiene habilitado y, de nivel 4
     * en adelante, el usuario tiene 3 directos activos. Los tres primeros no
     * piden nada.
     *
     * Es la regla que aplica el motor de liquidación al saltar los niveles 4+
     * cuando `levelOneActive < 3`. Acá estaba partida en dos: la tabla miraba
     * sólo `level.state` (la config global del bono, igual para todos) y los
     * totales sólo los 3 directos, así que a alguien sin directos activos le
     * salían los 7 niveles desbloqueados.
     */
    const isLevelLocked = (level) =>
        !level.state ||
        (Number(level.name) >= 4 && !hasEnoughActiveUsers);

    const maxPoints = Math.max(
        ...levels.map((level) => {
            const currencyPoints =
                totalLoyaltyPointsByLevel[level.name] || {};

            return currencyPoints[DISPLAY_CURRENCY] || 0;
        }),
        1
    );

    const totalPoints = levels.reduce((sum, level) => {
        if (isLevelLocked(level)) return sum;

        const currencyPoints =
            totalLoyaltyPointsByLevel[level.name] || {};

        return sum + (currencyPoints[DISPLAY_CURRENCY] || 0);
    }, 0);

    // Los dos totales de abajo NO descuentan los niveles bloqueados: son los
    // puntos que la red generó y lo que le corresponderían si estuviera al
    // día. Los que sí mandan para pagar siguen siendo `totalPoints` y
    // `totalCommission`, que respetan el bloqueo.
    const totalNetworkPoints = levels.reduce((sum, level) => {
        const currencyPoints =
            totalLoyaltyPointsByLevel[level.name] || {};

        return sum + (currencyPoints[DISPLAY_CURRENCY] || 0);
    }, 0);

    const totalNetworkCommission = levels.reduce((sum, level) => {
        const currencyPoints =
            totalLoyaltyPointsByLevel[level.name] || {};

        const points = currencyPoints[DISPLAY_CURRENCY] || 0;

        return sum + points * (level.porcentage / 100) * pointValue;
    }, 0);

    const totalCommission = levels.reduce((sum, level) => {
        if (isLevelLocked(level)) return sum;

        const currencyPoints =
            totalLoyaltyPointsByLevel[level.name] || {};

        const points =
            currencyPoints[DISPLAY_CURRENCY] || 0;

        return (
            sum +
            points *
            (level.porcentage / 100) *
            pointValue
        );
    }, 0);

    // Las regalías se liquidan por mes calendario.
    const periodStart = formatDate(pointsData.monthPeriod?.start).slice(0, 10);
    const periodEnd = formatDate(pointsData.monthPeriod?.end).slice(0, 10);

    return (
        <div>
            {periodStart && periodEnd && (
                <h6 className="fw-bold mb-3">
                    Periodo de corte de regalías {periodStart} a {periodEnd}
                </h6>
            )}
            <Card className="shadow-sm border-0 mb-4">
                <CardBody className="py-4">

                    <Row className="align-items-center">

                        <Col md="4">

                            <div className="d-flex align-items-center">
                                <div
                                    className="me-3"
                                    style={{
                                        width: 60,
                                        height: 60,
                                    }}
                                >
                                    <img
                                        src={`${API_BASE_URL}/users/show/photo/${user?.photo || ""}`}
                                        alt={user?.name || "Usuario"}
                                        style={{
                                            width: "60px",
                                            height: "60px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "2px solid #e9ecef",
                                        }}

                                    />
                                </div>
                                <div>

                                    <h5 className="mb-1 fw-bold">
                                        {user?.name || "Usuario"}
                                    </h5>

                                    <div className="small text-muted mb-1">
                                        Código:
                                        <span className="text-success fw-bold ms-1">
                                            {user?.multyhealth_code || "-"}
                                        </span>
                                    </div>

                                    {user?.network?.loyalty_status ? (
                                        <Badge color="success">
                                            Activo
                                        </Badge>
                                    ) : (
                                        <Badge color="secondary">
                                            Inactivo
                                        </Badge>
                                    )}

                                </div>

                            </div>

                        </Col>

                        <Col
                            md="2"
                            className="text-center border-start"
                        >

                            <div
                                className="text-muted"
                                style={{ fontSize: 13 }}
                            >
                                Rango Actual
                            </div>

                            <h4
                                className="fw-bold mt-2 mb-1"
                                style={{ color: "#f39c12" }}
                            >
                                {currentRankName}
                            </h4>

                            {/*
                              El rango sale de los puntos del mes, así que
                              decir cuántos faltan para el siguiente cierra la
                              lectura: el número de arriba se entiende con el
                              de abajo.
                            */}
                            <small className="text-muted">
                                {nextPointsRank
                                    ? `Faltan ${pointsToNextRank.toLocaleString()} pts para ${nextPointsRank.name}`
                                    : "Rango máximo alcanzado"}
                            </small>

                        </Col>

                        <Col
                            md="3"
                            className="text-center border-start"
                        >

                            <div
                                className="text-muted"
                                style={{ fontSize: 13 }}
                            >
                                Total de Directos
                            </div>

                            <h2 className="fw-bold mt-2">
                                {user?.user_network?.length || 0}
                            </h2>

                        </Col>

                        <Col
                            md="3"
                            className="text-center border-start"
                        >

                            <div
                                className="text-muted"
                                style={{ fontSize: 13 }}
                            >
                                Puntos Actuales
                            </div>

                            <h2
                                className="fw-bold mt-2 text-success"
                            >
                                {currentUserPoints.toLocaleString()}
                            </h2>

                        </Col>

                    </Row>

                </CardBody>
            </Card>

            <Card className="shadow-sm border-0">
                <CardBody className="p-0">

                    <Table
                        responsive
                        hover
                        className="align-middle mb-0"
                    >

                        <thead
                            style={{
                                background: "#f8f9fa",
                            }}
                        >
                            <tr>

                                <th style={{ width: "16%" }}>
                                    Nivel
                                </th>

                                <th style={{ width: "16%" }}>
                                    Estado
                                </th>

                                <th
                                    className="text-center"
                                    style={{ width: "18%" }}
                                >
                                    Usuarios Activos
                                </th>

                                <th
                                    className="text-end"
                                    style={{ width: "12%" }}
                                >
                                    Puntos
                                </th>

                                <th
                                    className="text-end"
                                    style={{ width: "20%" }}
                                >
                                    Comisiones Totales
                                </th>
                                <th
                                    className="text-end"
                                    style={{ width: "25%" }}
                                >
                                    Acciones
                                </th>

                            </tr>
                        </thead>

                        <tbody>
                            {levels.map((level) => {
                                const levelNumber = Number(level.name);

                                const isLocked = isLevelLocked(level);

                                const lockReason = !level.state
                                    ? "Nivel desactivado en el plan"
                                    : "Requiere 3 directos activos";

                                const pointsByCurrency =
                                    totalLoyaltyPointsByLevel[level.name] || {};

                                const points =
                                    pointsByCurrency[DISPLAY_CURRENCY] || 0;

                                const commission =
                                    points *
                                    (level.porcentage / 100) *
                                    pointValue;

                                const progress =
                                    maxPoints > 0
                                        ? (points / maxPoints) * 100
                                        : 0;

                                // "Activos" son los que efectivamente
                                // compraron, no todos los del nivel:
                                // `generators` sólo incluye a quien generó al
                                // menos una orden, mientras que `users_count`
                                // cuenta la red completa del nivel, comprara o
                                // no. Se muestran los dos para dar la
                                // proporción.
                                const levelDetail =
                                    pointsData?.ordersByLevel?.[level.name];

                                const activeUsers =
                                    levelDetail?.generators?.length ?? 0;

                                const levelUsers =
                                    levelDetail?.users_count ?? 0;

                                const levelOrders =
                                    levelDetail?.orders_count ?? 0;

                                const levelOrderIds =
                                    levelDetail?.order_ids || [];

                                return (
                                    <tr key={level.name}>

                                        <td>

                                            <div className="fw-bold">
                                                Nivel {level.name}
                                            </div>

                                            <small className="text-muted">
                                                {level.porcentage}%
                                            </small>

                                        </td>

                                        <td>

                                            <Badge
                                                color={
                                                    isLocked
                                                        ? "secondary"
                                                        : "success"
                                                }
                                                pill
                                                className="px-3 py-2"
                                            >
                                                {isLocked
                                                    ? "Bloqueado"
                                                    : "Desbloqueado"}
                                            </Badge>

                                            {isLocked && (
                                                <div className="small text-muted mt-1">
                                                    {lockReason}
                                                </div>
                                            )}

                                        </td>

                                        <td className="text-center">

                                            <div
                                                className={
                                                    activeUsers > 0
                                                        ? "fw-bold"
                                                        : "fw-bold text-muted"
                                                }
                                            >
                                                {activeUsers}
                                                {levelUsers > 0 && (
                                                    <span className="text-muted fw-normal">
                                                        {" "}/ {levelUsers}
                                                    </span>
                                                )}
                                            </div>

                                            <small className="text-muted">
                                                {levelOrders}{" "}
                                                {levelOrders === 1
                                                    ? "orden"
                                                    : "órdenes"}
                                            </small>

                                        </td>


                                        <td className="text-end">
                                            {points.toLocaleString()}
                                        </td>

                                        {/*
                                          El nivel bloqueado ya no muestra "-":
                                          se ve en gris lo que ese nivel le
                                          daría, que es lo que se pregunta
                                          cuando el usuario está inactivo. El
                                          badge de Estado sigue diciendo que no
                                          se le paga.
                                        */}
                                        <td
                                            className={
                                                isLocked
                                                    ? "text-muted text-end"
                                                    : "text-success text-end fw-bold"
                                            }
                                        >
                                            {formatCurrency(
                                                commission,
                                                DISPLAY_CURRENCY
                                            )}
                                        </td>

                                        {/*
                                          El icono se leía como
                                          `ordersByLevel[nivel].order_ids`
                                          directo: en un nivel sin órdenes
                                          `ordersByLevel[nivel]` no existe y el
                                          clic reventaba. Ahora sólo se pinta
                                          cuando hay algo que abrir.
                                        */}
                                        <td className="text-end">
                                            {levelOrderIds.length > 0 && (
                                                <i
                                                    className="fa fa-list me-2"
                                                    role="button"
                                                    title={`Ver ${levelOrderIds.length} ${levelOrderIds.length === 1 ? "orden" : "órdenes"}`}
                                                    onClick={() =>
                                                        setSelectedOrderIds(
                                                            levelOrderIds
                                                        )
                                                    }
                                                />
                                            )}
                                        </td>
                                         
                                                

                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>

                </CardBody>
            </Card>

            <Row className="mt-4">

                <Col md="6" className="mb-3">

                    <Card className="border-0 shadow-sm h-100">
                        <CardBody className="text-center">

                            <div
                                className="text-muted mb-2"
                                style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                }}
                            >
                                Total Puntos Generados
                            </div>

                            <h2
                                className="fw-bold text-success mb-0"
                                style={{
                                    fontSize: "2rem",
                                }}
                            >
                                {totalNetworkPoints.toLocaleString()}
                            </h2>

                            <small className="text-muted">
                                Los {levels.length} niveles, esté activo o no
                            </small>

                            <div className="small text-muted mt-2">
                                Le cuentan hoy:{" "}
                                <span className="fw-bold">
                                    {totalPoints.toLocaleString()}
                                </span>{" "}
                                pts
                            </div>

                        </CardBody>
                    </Card>

                </Col>

                <Col md="6" className="mb-3">

                    <Card className="border-0 shadow-sm h-100">
                        <CardBody className="text-center">

                            <div
                                className="text-muted mb-2"
                                style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                }}
                            >
                                Comisiones
                            </div>

                            <h2
                                className="fw-bold text-success mb-0"
                                style={{
                                    fontSize: "2rem",
                                }}
                            >
                                {formatCurrency(
                                    totalCommission,
                                    DISPLAY_CURRENCY
                                )}
                            </h2>

                            <small className="text-muted">
                                Está ganando
                            </small>

                            <div className="small text-muted mt-2">
                                Ganaría cumpliendo todo:{" "}
                                <span className="fw-bold">
                                    {formatCurrency(
                                        totalNetworkCommission,
                                        DISPLAY_CURRENCY
                                    )}
                                </span>
                            </div>

                        </CardBody>
                    </Card>

                </Col>

            </Row>
            <UserOrdersModal
                orderIds={validOrderIds}
                toggle={() => setSelectedOrderIds([])}
                ordersResponse={ordersResponse}
                loadingOrders={loadingOrders}
                pointsField="loyalty_points"
            />

        </div>
    );
};

export default LoyaltyTab;